import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../../hooks/useAuth';

const LoginForm = () => {
  const { login, error: authError, clearError } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Clear errors when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);

  useEffect(() => {
    if (authError) {
      const errorMessage =
        authError?.detail ||
        authError?.message ||
        (typeof authError === 'string' ? authError : null) ||
        'Invalid username or password. Please try again.';
      setLocalError(errorMessage);
    }
  }, [authError]);

  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
    setLocalError('');
    clearError();
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setLocalError('');
    clearError();
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsSubmitting(true);
    setLocalError('');
    clearError();

    try {
      await login(username, password);
      // On success, navigate to studies
      navigate('/studies', { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      // Error will be set via useEffect from authError
      // Also set local error as fallback
      const errorMessage =
        err?.detail ||
        err?.message ||
        (typeof err === 'string' ? err : null) ||
        'Invalid username or password. Please try again.';
      setLocalError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };



  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <form onSubmit={onSubmit} className="max-w-md w-full mx-auto p-8 bg-white rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Login</h2>

        {localError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{localError}</p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">Username</div>
            <input
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={username}
              onChange={handleUsernameChange}
              disabled={isSubmitting}
              required
              autoComplete="username"
            />
          </label>
          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">Password</div>
            <input
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              type="password"
              value={password}
              onChange={handlePasswordChange}
              disabled={isSubmitting}
              required
              autoComplete="current-password"
            />
          </label>
          <button
            className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Signing in…' : 'Login'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;
